/**
 * Ce que l'écran de campagne montre.
 *
 * Même partage qu'ailleurs dans le client : la décision vit ici, dans un module pur,
 * et le composant React ne fait que la rendre. Un comptoir verrouillé, un comptoir à
 * refaire pour son étoile et un comptoir terminé ne se distinguent que par ce que ce
 * fichier en dit — et c'est donc ici qu'on le teste.
 */

import {
  COMPTOIRS, avancement, comptoirOuvert, profilDe,
  type Progression, type Statistiques,
} from "@zinc421/shared";

import type { Textes } from "../i18n/index.js";
import { libelleDefi } from "../jeu/libelles.js";

export type EtatFiche = "VERROUILLE" | "A_FAIRE" | "GAGNE";

export interface FicheComptoir {
  readonly numero: number;
  readonly lieu: string;
  readonly accroche: string;
  /** Les noms des adversaires, tête d'affiche en premier. */
  readonly adversaires: readonly string[];
  /** Sièges occupés, le joueur compris. */
  readonly joueurs: number;
  readonly soiree: boolean;
  readonly defi: string;
  readonly etat: EtatFiche;
  readonly etoile: boolean;
  readonly tentatives: number;
  /**
   * Un comptoir gagné reste jouable, et c'est volontaire : l'étoile se cherche en y
   * retournant. Le déblocage ne dépend d'aucun compteur de progression (§ campagne),
   * donc rejouer ne fait rien reculer.
   */
  readonly jouable: boolean;
}

export function fichesComptoirs(
  progression: Progression, textes: Textes,
): readonly FicheComptoir[] {
  return COMPTOIRS.map((comptoir) => {
    const etape = progression.etapes[comptoir.numero - 1];
    const ouvert = comptoirOuvert(progression, comptoir.numero);
    const gagne = etape?.gagnee ?? false;
    return {
      numero: comptoir.numero,
      lieu: textes.comptoirNom(comptoir.cle),
      accroche: textes.comptoirAccroche(comptoir.cle),
      adversaires: comptoir.adversaires.map((n) => textes.adversaireNom(profilDe(n).cle)),
      joueurs: comptoir.adversaires.length + 1,
      soiree: comptoir.soiree,
      defi: libelleDefi(comptoir.defi, textes),
      etat: !ouvert ? "VERROUILLE" : gagne ? "GAGNE" : "A_FAIRE",
      etoile: etape?.defiReussi ?? false,
      tentatives: etape?.tentatives ?? 0,
      jouable: ouvert,
    };
  });
}

export function resumeAvancement(progression: Progression, textes: Textes): string {
  const etat = avancement(progression);
  if (etat.termine) {
    return etat.etoiles === etat.total
      ? textes.tourneeToutesEtoiles
      : textes.tourneeFinie(etat.etoiles, etat.total);
  }
  return textes.avancement(etat.gagnes, etat.total, etat.etoiles);
}

export interface LigneStatistique {
  readonly libelle: string;
  readonly valeur: string;
}

/**
 * Les statistiques, réduites à celles qui disent quelque chose.
 *
 * Un compteur à zéro n'apprend rien et remplit l'écran d'un joueur qui débute — les
 * lignes vides sont donc tues. La seule exception est le couple parties/victoires :
 * il donne le taux, qui est la statistique que tout le monde cherche en premier.
 */
export function lignesStatistiques(
  stats: Statistiques, textes: Textes,
): readonly LigneStatistique[] {
  if (stats.parties === 0) return [];

  const lignes: LigneStatistique[] = [
    { libelle: textes.statParties, valeur: String(stats.parties) },
    {
      libelle: textes.statGagnees,
      valeur: textes.statPourcentage(
        stats.victoires, Math.round((stats.victoires / stats.parties) * 100)),
    },
  ];

  const ajouter = (libelle: string, valeur: number) => {
    if (valeur > 0) lignes.push({ libelle, valeur: String(valeur) });
  };

  ajouter(textes.statTournees, stats.tournees);
  ajouter(textes.statManches, stats.manches);
  ajouter(textes.stat421, stats.quatreCentVingtEtUn);
  ajouter(textes.statNenettes, stats.nenettes);
  ajouter(textes.statSecs, stats.secs);
  ajouter(textes.statRampeaux, stats.rampeaux);
  ajouter(textes.statCharge, stats.coupsEnCharge);
  ajouter(textes.statDecharge, stats.coupsEnDecharge);
  ajouter(textes.statJetons, stats.jetonsRamasses);

  if (stats.meilleurCoup !== null) {
    lignes.push({ libelle: textes.statMeilleurCoup, valeur: stats.meilleurCoup });
  }
  return lignes;
}
